'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { SearchComposer, type AttachedResumeInfo } from './SearchComposer';
import { SearchExplainer } from './SearchExplainer';
import { pickRandomSuggestions } from './mocks';
import type { SearchFilterGroup } from '../types';

interface SearchHeroProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  onPickSuggestion: (suggestion: string) => void;
  filterGroups?: SearchFilterGroup[];
  onFilterGroupsChange?: (groups: SearchFilterGroup[]) => void;
  attachedResume?: AttachedResumeInfo | null;
  onAddResume?: () => void;
  onRemoveResume?: () => void;
  disabled?: boolean;
  className?: string;
}

export function SearchHero({
  value,
  onChange,
  onSubmit,
  onPickSuggestion,
  filterGroups,
  onFilterGroupsChange,
  attachedResume,
  onAddResume,
  onRemoveResume,
  disabled,
  className,
}: SearchHeroProps) {
  // Stable random pick per mount — changes on each page load/navigation
  const suggestions = useMemo(() => pickRandomSuggestions(4), []);

  return (
    <div className={cn('mx-auto w-full space-y-4', className)}>
      <SearchComposer
        value={value}
        onChange={onChange}
        onSubmit={onSubmit}
        filterGroups={filterGroups}
        onFilterGroupsChange={onFilterGroupsChange}
        attachedResume={attachedResume}
        onAddResume={onAddResume}
        onRemoveResume={onRemoveResume}
        disabled={disabled}
        variant="hero"
      />

      <div className="flex flex-wrap items-center gap-2">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => onPickSuggestion(suggestion)}
            className={cn(
              'rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground/85 transition-all',
              'hover:-translate-y-px hover:border-primary/60 hover:text-foreground hover:shadow-sm',
            )}
          >
            {suggestion}
          </button>
        ))}
      </div>

      <SearchExplainer />
    </div>
  );
}
